<template>
  <main
    id="page-search"
    class="bg-bgr shadow-inner"
    :data-filters-count="Object.keys(searchState).length"
  >
    <div ref="pageTopAnchor" class="h-0" />

    <header>
      <div class="py-4 px-2 md:py-6" :class="contentWidthClass">
        <RedesignSearchBar
          v-model="searchBarState"
          collapsable
          class="md:w-fit"
          @submit="handleSubmit"
        />
      </div>
      <div v-if="!screen.isSmall" class="border-b"></div>
    </header>

    <section
      v-if="$params.iframe"
      class="grid grid-cols-2 items-center justify-between"
      :class="contentWidthClass"
    >
      <SearchBreadcrumb :geo="geo" />
      <BookmarkLink class="justify-self-end" />
    </section>

    <section
      class="relative px-2 md:pt-6 grid md:grid-cols-[24rem_1fr] md:gap-6"
      :class="contentWidthClass"
    >
      <aside class="flex flex-col gap-4 md:gap-6">
        <RedesignMapButton v-model="searchStateModel" :disabled="isLoading" />
        <RedesignFilter
          v-if="!screen.isSmall"
          v-model="searchStateModel"
          class="border rounded-md"
        />
      </aside>

      <section
        class="grid gap-6 auto-rows-min"
        :aria-busy="isLoading"
        data-id="search-result"
      >
        <template v-if="isLoading || total">
          <RedesignSearchAreaResults
            :items
            :trip
            :loading="isLoading || isFetching"
          >
            <template #title>
              <section v-if="total" class="grid gap-4">
                <div v-if="screen.isSmall" class="border-b -mx-4"></div>
                <SearchResultsHeader :total />
                <SearchResultsHeaderCount :total />
              </section>
            </template>
            <template #toolbar>
              <div class="grid grid-cols-[auto_1fr] gap-2 items-center">
                <RedesignFilterButton
                  v-if="screen.isSmall"
                  v-model="searchStateModel"
                  @on-opened="handleOpenFilterMobile"
                />
                <RedesignSearchSorting
                  v-model="searchState.sorting"
                  rounded
                  mode="right"
                  class="min-w-0"
                />
              </div>
            </template>
          </RedesignSearchAreaResults>
          <RedesignSearchPagination
            v-if="!isLoading"
            v-model="currentPage"
            :total="totalPages"
          />
        </template>
        <template v-else>
          <div class="grid gap-4">
            <RedesignFilterButton
              v-if="screen.isSmall"
              v-model="searchStateModel"
              @on-opened="handleOpenFilterMobile"
            />
            <div v-if="screen.isSmall" class="border-b -mx-4 mt-0"></div>
            <RedesignSearchAreaNoResults v-model="searchStateModel" />
          </div>
        </template>
      </section>
    </section>

    <SearchRatingBox :key="filtersKey" :filters />

    <SearchTeaser v-if="!$params.iframe" :search-facets="facets">
      <RedesignSearchBreadcrumb :items="breadcrumbs" />
      <template #searchteaser>
        <SearchObjectTeasers
          v-if="objectTeasersEnabled"
          :key="destinationFilterKey"
          :show="objectTeasersVisible"
          :destination="destination"
          :season
        />
      </template>
      <template #contentassets>
        <SearchContentAssets
          v-if="contentAssetsEnabled"
          :key="destinationFilterKey"
          :filter="destinationFilter"
        />
      </template>
    </SearchTeaser>
    <USPCards :why="additionalBody.USPCards.cards" />
    <USPWhy
      :why="[additionalBody.USPColumns.left, additionalBody.USPColumns.right]"
    />
    <FooterNewsletterSignup />
  </main>
</template>

<script setup lang="ts">
import type { DeepReadonly } from 'vue'
import type { SearchBarState } from '~/composables/search.types'

const OBJECT_TEASERS_MIN_RESULTS_TO_DISPLAY = 10
const OBJECT_TEASERS_FILTERS_TO_DISPLAY = new Set([
  'sea',
  'pool',
  'pets',
  'skiing',
  'familyfriendly',
])
const CONTENT_ASSETS_FILTERS_TO_DISPLAY = new Set([
  'sea',
  'pool',
  'pets',
  'familyfriendly',
])

const confData = useConfdata()
const search = useSearch() // TODO: don't forget to remove legacy search dependency later, not critical now
const ab = useAB()
const screen = useScreen()
const tracking = useTracking()
const { contentWidthClass } = useContentWidth()

useRedesignPageOverflow('hidden')

const isContentAssetsEnabled = useFlag('content-assets')

const confDataRaw = computed(() => confData.raw!)
const confCurrentRoute = computed(() => confData.currentRoute!)
const initialState = getSearchStateFromRouteConf(confCurrentRoute, confDataRaw)
if (!initialState)
  throw createError({ statusMessage: 'Search state is not defined' })
const searchState = useSearchState(initialState, handleFilterChange)
const searchStateModel = searchState.model
const searchResults = useSearchResults(searchState)
const {
  key: resultsKey,
  items,
  facets,
  isLoading,
  isFetching,
  total,
  currentPage,
  totalPages,
} = toRefs(searchResults)
const season = useSearchSeason(searchState)
const breadcrumbs = useSearchDestinationBreadcrumbs(confCurrentRoute)

const page = computed(() => searchState.page)
const geo = computed(() => search.geoCurrent)
const additionalBody = computed(() => confData.additionalBody as SearchPageBody)
const destination = computed(() => confData.destination)
const destinationFilter = computed(() => destination.value?.filters?.[0].key)
const destinationFilterKey = computed(() => destination.value.urlPath)
const route = useRoute()

const searchBarState = ref<SearchBarState>(getSearchBarState(searchState))

function getSearchBarState(state: SearchState) {
  return {
    destination: mapDestinationToSuggestion(confDataRaw.value.destination),
    period: mapDatesStateToDatePeriod(state.dates),
    guests: {
      pax: state.pax ?? PAX_MIN,
      pets: state.minpets || Number(state.pets ?? PETS_MIN),
    },
  }
}

const trip = computed(() => getTripParamsFromState(searchState))

const filters = computed(() => mapSearchStateToSearchApiParams(searchState))

const filtersKey = computed(() => JSON.stringify(searchState))
const objectTeasersEnabled = computed(() =>
  Boolean(
    ab.isActive('objectteaser') &&
      route.path && // handeleing reactivity, see https://jira.migros.net/browse/IHGWEBCC-1382
      confData.isCanonicalPage &&
      (!destinationFilter.value ||
        OBJECT_TEASERS_FILTERS_TO_DISPLAY.has(destinationFilter.value)),
  ),
)
const objectTeasersVisible = computed(
  () =>
    objectTeasersEnabled.value &&
    total.value >= OBJECT_TEASERS_MIN_RESULTS_TO_DISPLAY,
)

const contentAssetsEnabled = computed(() =>
  Boolean(
    isContentAssetsEnabled.value &&
      destination.value?.countryCode &&
      (!destinationFilter.value ||
        CONTENT_ASSETS_FILTERS_TO_DISPLAY.has(destinationFilter.value)),
  ),
)

const applySearchBarState = useSearchBarToSearchStateApply()

async function handleSubmit(submittedState: DeepReadonly<SearchBarState>) {
  const newState = await applySearchBarState(submittedState, searchStateModel)

  searchState.update(newState)
}

const pageTopAnchor = ref<HTMLDivElement>()

function scrollToTopAnchor() {
  pageTopAnchor.value?.scrollIntoView({
    behavior: screen.isSmall ? 'instant' : 'smooth',
  })
}

function handleOpenFilterMobile() {
  tracking.handlers?.redesignFilters.filterMobile(false)
}

watch(page, (pageParamValue) => {
  if (!pageParamValue) return

  scrollToTopAnchor()
})

/**
 * Tracking each new results, even if they match the old ones.
 * For compatibility with the old solution.
 * @see https://jira.migros.net/browse/IHGWEBCC-2327
 */
watch(resultsKey, () => {
  tracking.handlers?.search.results(destination.value, trip.value, total.value)
  tracking.handlers?.ecommerce.viewItemList(items.value)
})

function handleFilterChange(updatedState: SearchState) {
  const searchUrl = generateSearchUrl(confDataRaw, updatedState)
  navigateTo(withShallowRoute(getRouteFromPath(getUrlFullPath(searchUrl))))
}

onFullRouteUpdate((to) => {
  const routeSearchState = getSearchStateFromRouteConf(to, confDataRaw)

  if (!routeSearchState) return

  searchState.reset(routeSearchState)
  searchBarState.value = getSearchBarState(routeSearchState)

  scrollToTopAnchor()
})

onMounted(() => {
  window.onbeforeunload = function () {
    // Before a page-reload the browser normally memorises the scroll-position, but on the reloaded searchpage this may have unwanted sideeffects cause of dynamic content.
    // So it is necessary to set the scroll-position to 0 before unload (which leads to position top on reload)
    window.scrollTo(0, 0)
  }
})
</script>
